import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CallAPI } from 'actions/General';
import Error from 'components/Error';
import TableComponent from 'components/table-component/Table';
import { archivedFormsTableHeader, formsTableHeader, formsTabsList } from './utils';
import TabLayout from 'components/tab-components/TabLayout';
import TabBody from 'components/tab-components/TabBody';
import TabHeader from 'components/tab-components/TabHeader';
import FormsListHeader from './FormsListHeader';
import { cloneDeep } from 'lodash';
import { permissionKeys } from 'config/config';
import { isPermissionAvailable } from 'config/permissionUtils';
import Pagination from 'components/common-components/Pagination';
import ScreenOverlay from 'components/common-components/ScreenOverlay';

const FormList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const condition = sessionStorage.getItem('formListQuery')
    ? JSON.parse(sessionStorage.getItem('formListQuery'))
    : null;

  const [allForms, setAllForms] = useState([]);
  const [selectedTab, setSelectedTab] = useState(formsTabsList[0]);
  const [totalForms, setTotalForms] = useState(0);
  const [currentPage, setCurrentPage] = useState(condition?.page ?? 1);
  const [displayEntries, setDisplayEntries] = useState(condition?.limit ?? 10);
  const [loader, setLoader] = useState(false);
  const [APIError, setAPIError] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState(condition?.searchText ?? '');
  const [searchTextData, setSearchTextData] = useState(condition?.searchText ?? '');
  const [filteringData, setFilteringData] = useState(
    condition?.filterFolderType
      ? { filterFolderType: condition.filterFolderType, filterFolderIds: condition.filterFolderIds }
      : { filterFolderIds: [] },
  );
  const [folderFilterOpen, setFolderFilterOpen] = useState(
    !!(condition?.filterFolderType ?? false),
  );

  const getAllForms = async (page = 1, limit = 10) => {
    setSelectedRows([]);
    let result = await CallAPI(
      'FETCH_FORMS',
      {
        screenTab: selectedTab.value,
        page: page,
        limit: limit,
        searchText: searchTextData,
        ...filteringData,
        ...(condition ?? {}),
      },
      null,
      setLoader,
      setAPIError,
      null,
    );
    if (result.status) {
      sessionStorage.removeItem('formListQuery');
      let formsList = [];
      result.data.forms.map((singleForm) => {
        formsList.push({
          ...singleForm,
          currentVersion: singleForm.currentVersion ? singleForm.currentVersion : {},
          formFilterId: filteringData.filterFolderIds,
          publishedDate:
            singleForm.publishedVersion && singleForm.publishedVersion.publishedDate
              ? singleForm.publishedVersion.publishedDate
              : '',
          folderName: singleForm.folder?.name ?? '--',
        });
      });

      setAllForms(formsList);
      setTotalForms(result.data.totalCount);
    }
  };
  const editForm = (rowId) => {
    navigate('/forms/edit', {
      relative: 'path',
      state: { type: 'edit', formId: rowId },
    });
  };
  const navigateToForms = () => {
    navigate('/forms/new', {
      relative: 'path',
      state: { type: 'add' },
    });
  };

  useEffect(() => {
    setSelectedRows([]);

    if (isPermissionAvailable(permissionKeys.field_work_forms_read)) {
      getAllForms();
    }
  }, [selectedTab, filteringData, searchTextData]);

  const onRowsSelection = (name, value) => {
    let selectedIds = cloneDeep(selectedRows);
    if (name === 'all') {
      allForms.map((single_form) => {
        if (value && selectedIds.map((e) => e.id).indexOf(single_form._id) === -1)
          selectedIds.push({ id: single_form._id, title: single_form.title });
        if (!value) selectedIds = [];
        setSelectedRows(selectedIds);
      });
    } else {
      if (value) {
        let title = '';
        allForms.map((single_form) => {
          if (single_form._id === name) title = single_form.title;
        });
        selectedIds.push({ id: name, title: title });
      } else selectedIds.splice(selectedIds.map((e) => e.id).indexOf(name), 1);
      setSelectedRows(selectedIds);
    }
  };

  const updateFilteringData = (name, value) => {
    let filteringDataObj = { filterFolderIds: [] };
    if (value) {
      if (name === 'all' || name === 'no_folder') {
        filteringDataObj.filterFolderType = name;
      } else if (name === 'custom') {
        filteringDataObj.filterFolderIds = [value];
        filteringDataObj.filterFolderType = 'custom';
      }
    }

    setCurrentPage(1);
    setDisplayEntries(10);
    setFilteringData(filteringDataObj);
  };
  const debouncedFunction = (name, value) => {
    setSearchTextData(value);
  };
  const searchTextChanged = (name, value) => {
    setSearchText(value);
    setCurrentPage(1);
    setDisplayEntries(10);
  };

  const formNameClickCallBack = (row) => {
    navigate('/forms/edit', {
      relative: 'path',
      state: { type: 'edit', formId: row._id },
    });

    const formCondition = {
      page: currentPage,
      limit: displayEntries,
      searchText: searchTextData,
    };
    if (filteringData.filterFolderType)
      formCondition.filterFolderType = filteringData.filterFolderType;
    if (filteringData.filterFolderIds)
      formCondition.filterFolderIds = filteringData.filterFolderIds;

    sessionStorage.setItem('formListQuery', JSON.stringify(formCondition));
  };

  const changeDisplayEntries = (_, val) => {
    setCurrentPage(1);
    setDisplayEntries(parseInt(val));
    getAllForms(1, parseInt(val));
  };

  const formDataMap = useMemo(() => {
    return Object.fromEntries(
      allForms.map((form) => [
        form._id,
        {
          folderId: form.folder?._id ?? null,
          formStatus: form.formStatus,
        },
      ]),
    );
  }, [allForms]);

  return (
    <TabLayout>
      {loader && totalForms === 0 && <ScreenOverlay extended />}
      <TabHeader
        tabList={formsTabsList}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
      <TabBody>
        {APIError ? <Error msg={APIError} /> : <></>}
        <FormsListHeader
          actionFunction={selectedTab.value === 'forms' ? navigateToForms : null}
          selectedTab={selectedTab.value}
          setSearchText={searchTextChanged}
          searchText={searchText}
          selectedRows={selectedRows}
          getAllForms={getAllForms}
          currentPage={currentPage}
          debounceFunc={debouncedFunction}
          filteringData={filteringData}
          formDataMap={formDataMap}
        />
        <TableComponent
          data={allForms}
          header={selectedTab.value === 'archived' ? archivedFormsTableHeader : formsTableHeader}
          props={{
            type: 'form_list',
            editForm,
            actionsCallBack: getAllForms,
            actionsType: selectedTab.value,
            updateFilteringData,
            formNameClickCallBack,
            translateValueFunc: (val) => t(val),
            folderFilterOpen,
            setFolderFilterOpen,
          }}
          totalRecords={totalForms}
          filterEnabled={false}
          loader={loader}
          filteringData={filteringData}
          isFieldSelectionEnabled={false}
          isPaginationEnabled={false}
          isActionsEnabled={true}
          isSelectionEnabled={true}
          selectionKey={'_id'}
          onSelection={onRowsSelection}
          selectedRows={selectedRows}
        />
        {totalForms > 0 && (
          <Pagination
            totalRecords={totalForms}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            displayEntries={displayEntries}
            setDisplayEntries={changeDisplayEntries}
            currentPageRecords={allForms.length}
            paginationFunction={getAllForms}
          />
        )}
      </TabBody>
    </TabLayout>
  );
};

export default FormList;
